import React from 'react'
import {
  Box,
  Grid,
  ClickAwayListener,
  Stack,
  Typography,
  SvgIcon,
  IconButton,
} from '@mui/material'
import ImagesCarousel from '../Lists/ListCard/ImagesCarousel'
import ListingName from './ListingName'
import ListingNotes from './ListingNotes'
import { AdminPropertyAdWithList } from './types'
import ListingIcons from './ListingIcons'
import { PROPERTY_AD_STATUS } from 'helpers/constants'
import ListingDelete from './ListingDelete'
import { PropertyAttributeEnum } from 'types/enums'
import { i18nLocationName } from 'types'
import { useAuthStore } from 'store'
import { ReactComponent as PhoneSvg } from 'assets/svg/new-icons/phoneblack.svg'
import { ReactComponent as EmailSvg } from 'assets/svg/new-icons/emailblack.svg'
import ListingLink from './ListingLink'
import ListingAgentLink from './ListingAgentLink'
import { useTranslation } from 'react-i18next'

interface Props {
  propertyAd: AdminPropertyAdWithList
  onDelete: () => void
  matchesSM: boolean
  matchesTablet: boolean
  matchesLaptop: boolean
  onUpdate: (
    name: string | null,
    notes: string | null,
    propertyAdAlternativeId: string | null
  ) => void
  listId: number | null
}
const ListingTabletView = (props: Props) => {
  const {
    propertyAd,
    onDelete,
    listId,
    onUpdate,
    matchesSM,
    matchesTablet,
    matchesLaptop,
  } = props
  const { t } = useTranslation()

  const [isExpanded, setIsExpanded] = React.useState(false)

  const handleClickAway = () => {
    if (isExpanded) {
      setIsExpanded(false)
    }
  }

  const handleClickIn = () => {
    if (!isExpanded) setIsExpanded(true)
  }

  const PROPERTY_IS_INACTIVE =
    propertyAd.status !== PROPERTY_AD_STATUS.STATUS_ACTIVE
  const locale = useAuthStore((state) => state.locale)

  const LOCALISED_LOCATION_NAME = React.useMemo(() => {
    return propertyAd?.properties?.[0]?.location?.searchLocation?.i18nLocationName?.find(
      (item) => item.locale === locale
    ) as i18nLocationName
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  const PROPERTY_TYPE = React.useMemo(() => {
    return propertyAd?.properties?.[0]?.propertyAttributes?.find((item) =>
      [1, 4].includes(propertyAd.adCategory.id)
        ? item.type.description === PropertyAttributeEnum.ESTATE_PROPERTY_TYPE
        : [2, 5].includes(propertyAd.adCategory.id)
        ? item.type.description ===
          PropertyAttributeEnum.ESTATE_PROPERTY_COMMERCIAL_TYPE
        : item.type.description ===
          PropertyAttributeEnum.ESTATE_PROPERTY_LAND_TYPE
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <Box
      sx={{
        pointerEvents: PROPERTY_IS_INACTIVE ? 'none' : 'auto',
        zIndex: 1,
        position: 'relative',
      }}
    >
      {PROPERTY_IS_INACTIVE ? (
        <Box
          sx={{
            position: 'absolute',
            display: 'grid',
            placeItems: 'center',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 2,
            fontWeight: 700,
            fontSize: '16px',
          }}
        >
          {t(`ListPage.inactive`)}
        </Box>
      ) : null}
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box
          sx={{
            width: '100%',
            opacity: PROPERTY_IS_INACTIVE ? '0.4' : '1',
          }}
          onClick={() => (isExpanded ? handleClickAway() : handleClickIn())}
        >
          <Grid
            container
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={{ xs: '12px', sm: '20px' }}
            sx={{
              bgcolor: 'common.white',
              borderRadius: '20px',
              borderColor: 'secondary',
              boxShadow: '8px 8px 20px 0px #09090959',
              p: '10px',
              flexWrap: 'no-wrap',
              border: '3px solid transparent',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
              },
              transition: (theme) => theme.transitions.create(['border-color']),
            }}
          >
            <Grid item xs={12} direction="row">
              <Grid container>
                <Grid item xs="auto" onClick={(e) => e.stopPropagation()}>
                  <ImagesCarousel
                    options={
                      propertyAd?.attachments
                        ?.map((item) => item.filePath)
                        .filter((item): item is string => !!item) ?? []
                    }
                    width={120}
                    height={99}
                    errorHeight={isExpanded ? '130px' : '50px'}
                    errorWidth={isExpanded ? '130px' : '50px'}
                  />
                </Grid>

                <Grid item>
                  <Grid
                    container
                    direction="row"
                    sx={{
                      ml: '20px',
                      mt: '8px',
                      mb: '20px',
                    }}
                  >
                    <Grid item>
                      <ListingName
                        onUpdate={onUpdate}
                        name={propertyAd?.favorite?.name ?? ''}
                        notes={propertyAd?.favorite?.notes || null}
                        propertyAdAlternativeId={
                          propertyAd?.favorite?.entityId || null
                        }
                        listId={listId}
                        propertyAd={propertyAd}
                        matchesSM={matchesSM}
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    container
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Grid item>
                      <Grid item sx={{ mx: '20px' }}>
                        <Grid container direction="column">
                          <Typography
                            sx={{
                              width: '100%',
                              fontSize: '14px',
                              textAlign: 'left',
                              fontWeight: 500,
                              lineHeight: '16.4px',
                              height: '100%',
                            }}
                          >
                            {' '}
                            {`${t(
                              `enums.${PROPERTY_TYPE?.type.description}.${PROPERTY_TYPE?.valueText}.base`
                            )} ${
                              propertyAd.properties?.[0]?.squareMeters
                                ? t('generics.area', {
                                    count: Number(
                                      propertyAd?.properties?.[0]?.squareMeters.toFixed(
                                        2
                                      )
                                    ),
                                  })
                                : '-'
                            }`}{' '}
                          </Typography>
                        </Grid>
                        <Grid container>
                          <Typography
                            sx={{
                              width: '100%',
                              fontSize: '14px',
                              textAlign: 'right',
                              fontWeight: 500,
                              lineHeight: '16.4px',
                              height: '100%',
                            }}
                          >
                            {' '}
                            {LOCALISED_LOCATION_NAME.locationPath?.replaceAll(
                              '/',
                              ','
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item>
                      <Grid container direction="column">
                        <Typography
                          sx={{
                            width: '100%',
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '16.4px',
                            height: '100%',
                          }}
                        >
                          {`${
                            propertyAd.salesPrice
                              ? t('generics.currency', {
                                  count: Number(
                                    propertyAd.salesPrice.toFixed(2)
                                  ),
                                })
                              : '-'
                          }`}
                        </Typography>
                      </Grid>
                      <Grid container>
                        <Typography
                          sx={{
                            width: '100%',
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '16.4px',
                            height: '100%',
                          }}
                        >
                          {`${
                            propertyAd.salesPricePerSqrMeter &&
                            propertyAd.salesPricePerSqrMeter > 10
                              ? t('generics.priceToArea', {
                                  count: Math.trunc(
                                    Number(propertyAd.salesPricePerSqrMeter)
                                  ),
                                })
                              : propertyAd.salesPricePerSqrMeter &&
                                propertyAd.salesPricePerSqrMeter < 10
                              ? t('generics.priceToArea', {
                                  count: Number(
                                    propertyAd.salesPricePerSqrMeter.toFixed(1)
                                  ),
                                })
                              : '-'
                          }`}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {matchesTablet && !isExpanded ? (
              <Grid item xs={true} sx={{ ml: '-40px' }}>
                <Grid
                  container
                  sx={{
                    justifyItems: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                  }}
                >
                  <Grid item xs>
                    <ListingIcons
                      matchesSM={matchesSM}
                      matchesTablet={matchesTablet}
                      propertyAd={propertyAd}
                      isExpanded={isExpanded}
                      matchesLaptop={matchesLaptop}
                    />
                  </Grid>
                  <Grid item>
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      spacing={0}
                      gap={'20px'}
                    >
                      <ListingLink
                        propertyAd={propertyAd}
                        matchesTablet={matchesTablet}
                      />
                      <ListingDelete
                        listId={listId}
                        propertyAd={propertyAd}
                        onDelete={onDelete}
                        isExpanded={isExpanded}
                        listName={propertyAd?.favorite?.name ?? ''}
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Grid>
            ) : (
              <>
                <Grid item xs={12}>
                  <ListingIcons
                    matchesLaptop={matchesLaptop}
                    matchesSM={matchesSM}
                    matchesTablet={matchesTablet}
                    propertyAd={propertyAd}
                    isExpanded={isExpanded}
                  />
                </Grid>
                <Grid item xs>
                  <Grid
                    container
                    direction="row"
                    flexWrap="nowrap"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      spacing={0}
                      gap={'20px'}
                    >
                      <ListingLink
                        propertyAd={propertyAd}
                        matchesTablet={matchesTablet}
                      />
                      <ListingDelete
                        listId={listId}
                        propertyAd={propertyAd}
                        onDelete={onDelete}
                        isExpanded={isExpanded}
                        listName={propertyAd?.favorite?.name ?? ''}
                      />
                    </Stack>
                    <Grid item xs>
                      <Grid
                        container
                        direction="row"
                        alignContent="center"
                        justifyContent="center"
                      >
                        <ListingAgentLink
                          matchesSM={matchesSM}
                          propertyAd={propertyAd}
                        />
                      </Grid>
                    </Grid>
                    <Grid item>
                      <IconButton
                        onClick={() => {
                          const phoneNumber =
                            propertyAd?.agentCInfo?.mobileNumber ||
                            propertyAd?.agentCInfo?.landLineNumber ||
                            propertyAd?.agentCInfo?.landLineNumber2

                          if (phoneNumber) window.open(`tel:${phoneNumber}`)
                        }}
                        sx={{
                          mr: '18px',
                          minWidth: '50px',
                          minHeight: '50px',
                          border: {
                            xs: 'none',
                            sm: '1px solid #EBEBEB',
                            md: '1px solid #EBEBEB',
                          },
                          borderRadius: '12px',
                          boxShadow: {
                            xs: 'none',
                            sm: '3px 3px 8px rgba(9, 9, 9, 0.3)',
                            md: '3px 3px 8px rgba(9, 9, 9, 0.3)',
                          },
                          '&:hover': { bgcolor: 'transparent' },
                        }}
                      >
                        <SvgIcon
                          component={PhoneSvg}
                          inheritViewBox
                          sx={{ width: '16px' }}
                        />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          if (propertyAd?.agentCInfo?.email !== '')
                            window.open(
                              `mailto:${propertyAd?.agentCInfo?.email}`
                            )
                        }}
                        sx={{
                          minWidth: '50px',
                          minHeight: '50px',
                          border: {
                            xs: 'none',
                            sm: '1px solid #EBEBEB',
                            md: '1px solid #EBEBEB',
                          },
                          borderRadius: '12px',
                          boxShadow: {
                            xs: 'none',
                            sm: '3px 3px 8px rgba(9, 9, 9, 0.3)',
                            md: '3px 3px 8px rgba(9, 9, 9, 0.3)',
                          },
                          '&:hover': { bgcolor: 'transparent' },
                        }}
                      >
                        <SvgIcon
                          component={EmailSvg}
                          inheritViewBox
                          sx={{ width: '28px' }}
                        />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}
            {isExpanded ? (
              <Grid
                item
                xs={12}
                sx={{
                  mt: { xs: '0px' },
                  display: { xs: 'flex-item' },
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <ListingNotes
                  onUpdate={onUpdate}
                  notes={propertyAd?.favorite?.notes ?? ''}
                  name={propertyAd?.favorite?.name ?? null}
                  propertyAdAlternativeId={
                    propertyAd?.favorite?.entityId ?? null
                  }
                  listId={listId}
                />
              </Grid>
            ) : null}
          </Grid>
        </Box>
      </ClickAwayListener>
    </Box>
  )
}

export default ListingTabletView
